import React from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { buildFullLink, getPath } from "common/routing";
import { extractFirstSubdomain, getAppPrefix } from "common/utils/url";
import { useGetUserAccountsQuery } from "../../generated/graphql";
import { useStatusToasts } from "../../hooks/useStatusToasts";
import {
  useEmailParamParser,
  usePrevLocationParamParser,
} from "../../utils/params";
import FullPageLoadingScreen from "../Common/FullPageLoadingScreen";
import { USER_LOGIN } from "./__queries__";
import LoginPage from "./LoginPage";

const UserLoginPage = () => {
  const { t } = useTranslation();
  const email = useEmailParamParser("provideCredentials");
  const history = useHistory();
  const { addErrorToast } = useStatusToasts();
  const prev = usePrevLocationParamParser("provideCredentials");
  const prefix = getAppPrefix();

  const { data: accounts, loading: accountsLoading } = useGetUserAccountsQuery({
    variables: {
      email: email!,
    },
    onError: () => {
      addErrorToast(
        "There was an issue fetching your accounts. Please try again.",
      );
    },
    skip: !email,
  });

  if (!email) {
    history.push("/login");

    return <FullPageLoadingScreen />;
  }

  if (accountsLoading) {
    return <FullPageLoadingScreen />;
  }

  const currentSubdomain = extractFirstSubdomain({
    url: window.location.href,
  });

  const currentAccount = accounts?.getUserAccountsByEmail?.find(
    account => account.subdomain === currentSubdomain,
  );

  const backUrl = buildFullLink("login", {
    params: {},
    queryParams: {
      prev,
    },
    prefix,
  });

  if (!currentAccount) {
    window.location.href = backUrl;
    return null;
  }

  return (
    <LoginPage
      loginMutation={USER_LOGIN}
      fallbackPath={getPath("map")}
      event="Logged in"
      initialTitle={t("sign-in-to-your-account")}
      showPasswordReset={true}
      startingEmail={email}
      accounts={accounts?.getUserAccountsByEmail ?? []}
      currentAccount={currentAccount}
    />
  );
};

export default UserLoginPage;
